import { Suggestion } from '@coveo/headless';
import { ClockIcon } from '@heroicons/react/24/outline';
import HeroIcon from 'components/helpers/Icons/HeroIcon/HeroIcon';
import classNames from 'classnames';
import {
  filterRecentSearchesByInput,
  highlightRecentSearchMatch,
} from '../utilities/recentSearches';

interface Props {
  suggestions: Suggestion[];
  recentSearches: string[];
  showClearRecentSearches: boolean;
  searchValue: string;
  onSelect: (value: string) => void;
  onClearRecentSearches: () => void;
}

export const AzBlueHeadlessAutoSuggestDropdown = ({
  suggestions,
  recentSearches,
  showClearRecentSearches,
  searchValue,
  onSelect,
  onClearRecentSearches,
}: Props) => {
  const MAX_TOTAL = 8;
  const MAX_RECENT = 3;

  const matchingRecent = filterRecentSearchesByInput(recentSearches, searchValue);
  const limitedRecent = matchingRecent.slice(0, MAX_RECENT);
  const remainingSlots = MAX_TOTAL - limitedRecent.length;
  const limitedSuggestions = suggestions.slice(0, remainingSlots);

  if (limitedRecent.length === 0 && limitedSuggestions.length === 0) {
    return null;
  }

  return (
    <ul
      className={classNames(
        'absolute',
        'top-full',
        'left-0',
        'mt-1',
        'bg-white',
        'border',
        'rounded',
        'shadow',
        'z-[9999]',
        'w-full',
        'max-w-full',
        'min-w-[200px]',
        'border-gray'
      )}
    >
      {limitedRecent.length > 0 && (
        <>
          {showClearRecentSearches && (
            <li className="flex justify-between items-center p-2 font-bold text-gray-500">
              <span>Recent Searches</span>
              <button onClick={onClearRecentSearches} className="text-blue hover:text-blue-700">
                Clear
              </button>
            </li>
          )}
          {limitedRecent.map((query, index) => (
            <li
              key={`recent-${index}`}
              className="flex items-center gap-2 p-2 hover:bg-gray-light cursor-pointer !w-full text-left transition-colors"
              onClick={() => onSelect(query)}
            >
              <ClockIcon className="w-5 h-5 text-black shrink-0" />
              <span
                className="flex-1 truncate"
                dangerouslySetInnerHTML={{
                  __html: highlightRecentSearchMatch(query, searchValue),
                }}
              />
            </li>
          ))}
        </>
      )}

      {limitedSuggestions.map((s) => (
        <li
          key={`suggestion-${s.rawValue}`}
          className="flex items-center gap-2 p-2 hover:bg-gray-light cursor-pointer !w-full text-left transition-colors"
          onClick={() => onSelect(s.rawValue)}
        >
          <HeroIcon size="xs" icon="MagnifyingGlassIcon" />
          <span
            className="flex-1 truncate"
            dangerouslySetInnerHTML={{ __html: s.highlightedValue }}
          />
        </li>
      ))}
    </ul>
  );
};
